<template>
  <div class="face-zoom-container">
    <b-overlay :show="loading" variant="transparent" rounded="sm">
      <div class="video-wrapper">
        <video
            ref="videoElement"
            autoplay
            muted
            playsinline
            :class="{ 'face-detected': faceDetected }"
            :style="videoStyle"
        ></video>
        <canvas ref="canvasElement" style="display: none;"></canvas>
        <div v-if="!cameraStarted" class="loading-overlay">
          <div class="loading-content">
            <b-spinner variant="primary"></b-spinner>
            <p class="mt-2">{{ loadingMessage }}</p>
          </div>
        </div>
        <div
            v-if="faceDetected && showFaceBox"
            class="face-box"
            :style="faceBoxStyle"
        ></div>
        <div class="controls">
          <b-button
              v-if="!cameraStarted"
              variant="primary"
              :disabled="loading"
              @click="startCamera"
          >
            <b-spinner v-if="loading" small class="mr-1"></b-spinner>
            Kamerayi Baslat
          </b-button>
          <b-button v-if="cameraStarted" variant="danger" @click="stopCamera">
            Kamerayi Durdur
          </b-button>
          <b-button
              v-if="cameraStarted"
              variant="outline-secondary"
              class="ml-2"
              @click="toggleFaceBox"
          >
            {{ showFaceBox ? 'Kutuyu Gizle' : 'Kutuyu Goster' }}
          </b-button>
          <b-button
              v-if="cameraStarted"
              :variant="autoZoom ? 'success' : 'info'"
              class="ml-2"
              @click="cycleZoomLevel"
          >
            {{ manualZoomOverride ? 'Manuel' : 'Auto' }} Zoom: {{ zoomFactor }}x
          </b-button>
          <b-button
              v-if="cameraStarted"
              :variant="autoZoom ? 'outline-success' : 'outline-secondary'"
              class="ml-2"
              @click="toggleAutoZoom"
              size="sm"
          >
            {{ autoZoom ? 'Otomatik ✓' : 'Manuel' }}
          </b-button>
        </div>
        <div class="status-info">
          <span
              :class="[
              'status-indicator',
              faceDetected ? 'detected' : 'not-detected',
            ]"
          >
            {{ faceDetected ? 'Yuz Algilandi' : 'Yuz Algilanmadi' }}
          </span>
                     <div v-if="faceDetected && debugMode" class="debug-info">
             <small>
               X: {{ Math.round(faceBox ? faceBox.xMin : 0) }}-{{
                 Math.round(faceBox ? faceBox.xMax : 0)
               }}<br/>
               Y: {{ Math.round(faceBox ? faceBox.yMin : 0) }}-{{
                 Math.round(faceBox ? faceBox.yMax : 0)
               }}<br/>
               Boyut: {{ faceBox ? ((faceBox.width * faceBox.height) / (videoWidth * videoHeight) * 100).toFixed(1) : 0 }}%<br/>
               Zoom: {{ zoomFactor }}x {{ autoZoom && !manualZoomOverride ? '(Auto)' : '(Manuel)' }}
             </small>
           </div>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import { BButton, BOverlay, BSpinner } from 'bootstrap-vue'
import '@tensorflow/tfjs-backend-webgl'
import '@tensorflow/tfjs-backend-cpu'
import * as tf from '@tensorflow/tfjs-core'
import * as faceDetection from '@tensorflow-models/face-detection'

export default {
  name: 'FaceZoom',
  components: {
    BButton,
    BOverlay,
    BSpinner,
  },
  data() {
    return {
      loading: false,
      cameraStarted: false,
      stream: null,
      model: null,
      faceDetected: false,
      faceBox: null,
      detectionInterval: null,
      loadingMessage: 'Modeller yukleniyor...',
      showFaceBox: true,
      zoomFactor: 1,
      zoomLevels: [1, 1.5, 2, 2.5, 3],
      autoZoom: true,
      manualZoomOverride: false,
      videoWidth: 640,
      videoHeight: 480,
      isModelReady: false,
      debugMode: true,
    }
  },
  computed: {
    videoStyle() {
      if (!this.faceDetected || !this.faceBox || this.zoomFactor === 1) {
        return {
          transform: 'scale(1) translate(0, 0)',
          transformOrigin: 'center center',
        }
      }

      const faceCenterX = this.faceBox.xMin + this.faceBox.width / 2
      const faceCenterY = this.faceBox.yMin + this.faceBox.height / 2

      const normalizedX = faceCenterX / this.videoWidth
      const normalizedY = faceCenterY / this.videoHeight

      const translateX = ((0.5 - normalizedX) * 100) / this.zoomFactor
      const translateY = ((0.5 - normalizedY) * 100) / this.zoomFactor

      return {
        transform: `scale(${this.zoomFactor}) translate(${translateX}%, ${translateY}%)`,
        transformOrigin: 'center center',
        transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
      }
    },
    faceBoxStyle() {
      if (!this.faceBox) return {}

      const scale = 1 / this.zoomFactor

      return {
        left: this.faceBox.xMin + 'px',
        top: this.faceBox.yMin + 'px',
        width: this.faceBox.width + 'px',
        height: this.faceBox.height + 'px',
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      }
    },
  },
  async mounted() {
    await this.initializeTensorFlow()
  },
  beforeDestroy() {
    this.cleanup()
  },
      methods: {
      calculateAdaptiveZoom(faceBox) {
        if (!faceBox || !this.autoZoom || this.manualZoomOverride) {
          return this.zoomFactor
        }

        // Yüzün video içindeki oranını hesapla
        const faceArea = faceBox.width * faceBox.height
        const videoArea = this.videoWidth * this.videoHeight
        const faceRatio = faceArea / videoArea

        // İdeal yüz boyutu oranı (video alanının %15-20'si ideal)
        const idealFaceRatio = 0.175 // %17.5
        
        // Zoom faktörünü hesapla
        let adaptiveZoom = Math.sqrt(idealFaceRatio / faceRatio)
        
        // Zoom'u 1x ile 3x arasında sınırla
        adaptiveZoom = Math.max(1, Math.min(3, adaptiveZoom))
        
        // Smooth transition için mevcut zoom'a yakın değerler için hassasiyet azalt
        const difference = Math.abs(adaptiveZoom - this.zoomFactor)
        if (difference < 0.2) {
          adaptiveZoom = this.zoomFactor // Çok küçük değişiklikler için zoom'u sabit tut
        }
        
        return Math.round(adaptiveZoom * 10) / 10 // 0.1 hassasiyetinde yuvarla
      },
    async initializeTensorFlow() {
      this.loading = true
      this.loadingMessage = 'TensorFlow baslatiliyor...'

      try {
        const backends = ['webgl', 'wasm', 'cpu']
        let backendSet = false

        for (const backend of backends) {
          try {
            await tf.setBackend(backend)
            await tf.ready()
            backendSet = true
            console.log('TensorFlow.js backend set to:', backend)
            break
          } catch (e) {
            console.log('Failed to set backend:', backend, e.message)
          }
        }

        if (!backendSet) {
          throw new Error('No TensorFlow.js backend could be initialized')
        }

        const testTensor = tf.tensor([1, 2, 3, 4])
        testTensor.dispose()

        await this.loadModel()
      } catch (error) {
        console.error('TensorFlow initialization failed:', error)
        this.loadingMessage =
            'TensorFlow baslatilamadi. Lutfen sayfayi yenileyin.'
      }
    },

    async loadModel() {
      this.loadingMessage = 'Yuz algilama modeli yukleniyor...'

      try {
        this.model = await faceDetection.createDetector(
            faceDetection.SupportedModels.MediaPipeFaceDetector,
            {
              runtime: 'tfjs',
              modelType: 'short',
              maxFaces: 1,
              detectorModelUrl: undefined,
              solutionPath:
                  'https://cdn.jsdelivr.net/npm/@mediapipe/face_detection@0.4/',
            }
        )

        this.isModelReady = true
        this.loadingMessage = 'Model yuklendi, kamerayi baslatabilirsiniz'
        console.log('Face detection model loaded successfully')
      } catch (error) {
        console.error('Model loading failed:', error)
        this.loadingMessage =
            'Model yukleme basarisiz oldu. Lutfen sayfayi yenileyin.'
        this.isModelReady = false
      } finally {
        this.loading = false
      }
    },

    async startCamera() {
      if (!this.isModelReady || !this.model) {
        console.error('Model not loaded yet')
        if (this.$bvToast) {
          this.$bvToast.toast('Model henuz yuklenmedi, lutfen bekleyin.', {
            title: 'Uyari',
            variant: 'warning',
            solid: true,
          })
        }
        return
      }

      this.loading = true
      this.loadingMessage = 'Kamera baslatiliyor...'

      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: 'user',
          },
          audio: false,
        })

        const videoElement = this.$refs.videoElement
        const canvasElement = this.$refs.canvasElement

        if (videoElement) {
          videoElement.srcObject = this.stream

          videoElement.addEventListener('loadedmetadata', () => {
            this.videoWidth = videoElement.videoWidth
            this.videoHeight = videoElement.videoHeight

            if (canvasElement) {
              canvasElement.width = this.videoWidth
              canvasElement.height = this.videoHeight
            }

            console.log('Video dimensions:', this.videoWidth, 'x', this.videoHeight)

            this.cameraStarted = true
            this.loadingMessage = ''

            setTimeout(() => {
              this.startFaceDetection()
            }, 500)
          })
        }
      } catch (error) {
        console.error('Camera access failed:', error)
        this.loadingMessage = 'Kamera erisimi basarisiz oldu'

        if (this.$bvToast) {
          this.$bvToast.toast(
              'Kamera erisimi saglanamadi. Lutfen kamera izinlerini kontrol edin.',
              {
                title: 'Hata',
                variant: 'danger',
                solid: true,
              }
          )
        }
      } finally {
        this.loading = false
      }
    },

    stopCamera() {
      this.cleanup()
      this.cameraStarted = false
      this.faceDetected = false
      this.faceBox = null
      this.zoomFactor = 1
    },

    startFaceDetection() {
      if (this.detectionInterval) {
        clearInterval(this.detectionInterval)
      }

      this.detectFace()

      this.detectionInterval = setInterval(async () => {
        if (this.cameraStarted && this.isModelReady) {
          await this.detectFace()
        }
      }, 200)
    },

    async detectFace() {
      const videoElement = this.$refs.videoElement
      const canvasElement = this.$refs.canvasElement

      if (!videoElement || !this.model || !this.isModelReady) {
        return
      }

      if (videoElement.readyState !== 4) {
        return
      }

      try {
        const ctx = canvasElement.getContext('2d')

        ctx.drawImage(videoElement, 0, 0, this.videoWidth, this.videoHeight)

        const faces = await this.model.estimateFaces(canvasElement, {
          flipHorizontal: true,
        })

        if (faces && faces.length > 0) {
          const face = faces[0]

                     if (face.box && face.box.width > 0 && face.box.height > 0) {
             this.faceDetected = true

             this.faceBox = {
               xMin: this.videoWidth - face.box.xMax,
               xMax: this.videoWidth - face.box.xMin,
               yMin: face.box.yMin,
               yMax: face.box.yMax,
               width: face.box.width,
               height: face.box.height,
             }

             // Otomatik zoom hesapla ve uygula
             if (this.autoZoom && !this.manualZoomOverride) {
               const newZoom = this.calculateAdaptiveZoom(this.faceBox)
               if (newZoom !== this.zoomFactor) {
                 this.zoomFactor = newZoom
               }
             }

             if (this.debugMode) {
               console.log('Face detected at:', {
                 x: Math.round(this.faceBox.xMin) + '-' + Math.round(this.faceBox.xMax),
                 y: Math.round(this.faceBox.yMin) + '-' + Math.round(this.faceBox.yMax),
                 width: Math.round(this.faceBox.width),
                 height: Math.round(this.faceBox.height),
                 zoom: this.zoomFactor,
                 faceRatio: ((this.faceBox.width * this.faceBox.height) / (this.videoWidth * this.videoHeight) * 100).toFixed(1) + '%'
               })
             }
           } else {
             this.faceDetected = false
             this.faceBox = null
           }
        } else {
          this.faceDetected = false
          this.faceBox = null
          
          // Yüz algılanmadığında zoom'u 1x'e döndür
          if (this.autoZoom && !this.manualZoomOverride && this.zoomFactor !== 1) {
            this.zoomFactor = 1
          }
        }
      } catch (error) {
        console.warn('Face detection error:', error)
        this.faceDetected = false
        this.faceBox = null
      }
    },

    toggleFaceBox() {
      this.showFaceBox = !this.showFaceBox
    },

         cycleZoomLevel() {
       // Manuel zoom kullanıldığında otomatik zoom'u geçici olarak devre dışı bırak
       this.manualZoomOverride = true
       
       const currentIndex = this.zoomLevels.indexOf(this.zoomFactor)
       const nextIndex = (currentIndex + 1) % this.zoomLevels.length
       this.zoomFactor = this.zoomLevels[nextIndex]
       
       // 5 saniye sonra otomatik zoom'u tekrar aktif et
       setTimeout(() => {
         this.manualZoomOverride = false
       }, 5000)
     },

     toggleAutoZoom() {
       this.autoZoom = !this.autoZoom
       if (!this.autoZoom) {
         this.zoomFactor = 1
       }
     },

    cleanup() {
      if (this.detectionInterval) {
        clearInterval(this.detectionInterval)
        this.detectionInterval = null
      }

      if (this.stream) {
        this.stream.getTracks().forEach((track) => {
          track.stop()
        })
        this.stream = null
      }

      const videoElement = this.$refs.videoElement
      if (videoElement) {
        videoElement.srcObject = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.face-zoom-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
}

.video-wrapper {
  position: relative;
  width: 640px;
  height: 480px;
  background: #000;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 16px;
    transform: scaleX(-1);

    &.face-detected {
      box-shadow: 0 0 20px rgba(102, 126, 234, 0.5);
    }
  }

  canvas {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 16px;

  .loading-content {
    text-align: center;
    color: white;

    p {
      margin: 0;
      font-size: 16px;
      font-weight: 500;
    }
  }
}

.face-box {
  position: absolute;
  border: 3px solid #00ff00;
  border-radius: 8px;
  pointer-events: none;
  box-shadow: 0 0 10px rgba(0, 255, 0, 0.5);
  animation: pulse 2s infinite;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 10px rgba(0, 255, 0, 0.5);
  }
  50% {
    box-shadow: 0 0 20px rgba(0, 255, 0, 0.8);
  }
  100% {
    box-shadow: 0 0 10px rgba(0, 255, 0, 0.5);
  }
}

.controls {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 10;
}

.status-info {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10;
  text-align: right;

  .status-indicator {
    display: inline-block;
    padding: 8px 12px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);

    &.detected {
      background: rgba(0, 255, 0, 0.2);
      color: #00ff00;
      border: 1px solid #00ff00;
    }

    &.not-detected {
      background: rgba(255, 0, 0, 0.2);
      color: #ff4444;
      border: 1px solid #ff4444;
    }
  }

  .debug-info {
    margin-top: 5px;
    padding: 5px 10px;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    border-radius: 10px;
    font-family: monospace;
  }
}

@media (max-width: 768px) {
  .video-wrapper {
    width: 90vw;
    height: calc(90vw * 0.75);
    max-width: 480px;
    max-height: 360px;
  }

  .controls {
    flex-direction: column;
    align-items: center;
    bottom: 10px;

    button {
      width: 150px;
      margin: 2px 0;
    }
  }

  .status-info {
    top: 10px;
    right: 10px;

    .status-indicator {
      padding: 6px 10px;
      font-size: 12px;
    }
  }
}
</style>